import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getAccessToken } from '@/utils/jwt';

interface EnqueueJobRequest {
  worker: string;
  args?: Record<string, unknown>;
  queue?: string;
}

interface EnqueueJobResponse {
  job_id: string;
  state: string;
}

export interface JobError {
  at: string;
  error: string;
  attempt: number;
}

export interface JobRecord {
  id: string;
  state: string;
  queue: string;
  worker: string;
  meta: Record<string, unknown>;
  args: Record<string, unknown>;
  inserted_at: string | null;
  completed_at: string | null;
  errors: JobError[];
}

interface ListJobsResponse {
  jobs: JobRecord[];
}

export const jobsApi = createApi({
  reducerPath: 'jobsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGENT_MCP_URL || '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Job'],
  endpoints: (builder) => ({
    enqueueJob: builder.mutation<EnqueueJobResponse, EnqueueJobRequest>({
      query: (body) => ({
        url: '/jobs',
        method: 'POST',
        body,
      }),
    }),
    getJobStatus: builder.query<JobRecord, string>({
      query: (jobId) => `/jobs/${jobId}`,
      providesTags: (_r, _e, id) => [{ type: 'Job', id }],
    }),
    listJobs: builder.query<ListJobsResponse, undefined>({
      query: () => '/jobs',
      providesTags: ['Job'],
    }),
    listAllJobs: builder.query<JobRecord[], undefined>({
      query: () => '/jobs/admin',
      transformResponse: (response: { jobs: JobRecord[] }) => response.jobs,
      providesTags: ['Job'],
    }),
  }),
});

export const {
  useEnqueueJobMutation,
  useGetJobStatusQuery,
  useListJobsQuery,
  useListAllJobsQuery,
} = jobsApi;
